import { lazy } from "react";
import { createBrowserRouter, RouteObject } from "react-router-dom";

import { CurrentPlatformGuard } from "@/components/platform";
import {
  CurrentLeagueProvider,
  CurrentOrganizationProvider,
  CurrentPlatformProvider,
  CurrentUserProvider,
  OnboardingProvider,
  UserRolesProvider,
  TourProvider,
  useCurrentLeague,
} from "@/contexts";
import { TerminologyOverridesGate } from "@/i18n";
import { AuthLayout } from "@/layouts/AuthLayout";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { LeaguePageRedirect } from "@/pages/league/LeaguePage/LeaguePageRedirect";
import { RouteErrorBoundary } from "@/routes/RouteErrorBoundary";
import { NotFoundPage } from "@/ui";
import {
  importNamedComponent,
  MANAGER_REDIRECT_PATH,
  SCOPED_ROUTE_PREFIX_LEAGUE,
  SCOPED_ROUTE_PREFIX_ORG,
  SCOPED_ROUTE_PREFIX_PLATFORM,
} from "@/utils/common";

import { AuthGuard } from "../components/auth/AuthGuard";
import { GuestGuard } from "../components/auth/GuestGuard";
import { SuperAdminGuard } from "../components/auth/SuperAdminGuard";
import BrowseLayout from "../components/BrowseLayout";
import Loadable from "../components/Loadable";
import DisciplineCreate from "../pages/dashboard/DisciplineCreate";
import Disciplines from "../pages/dashboard/Disciplines";

import { AuthSplashScreenProvider } from "./AuthSplashScreenProvider";
import { Providers } from "./Providers";
import { RootErrorBoundary } from "./RootErrorBoundary";
import { stripLeagueShadowedSegments } from "./scopedRoute.utils";
import { ScopedRouteEnforcer } from "./ScopedRouteEnforcer";
import { SplashScreenProvider } from "./SplashScreenProvider";
import {
  redirectLegacyTournamentDetail,
  redirectTournamentRootToManager,
} from "./tournamentRouteRedirects";

// Register every authenticated route under all three scope prefixes plus the
// unscoped path. Most-specific first so react-router ranks the deepest match
// highest.
// Segments that have a dedicated league-scoped page (matched via the org-prefix
// mount below as an explicit `league/:id/<segment>` route) must NOT also be
// mounted as their GLOBAL page under the league prefix. Otherwise the global
// block shadows the league page at `…/league/:id/<segment>` — its index route
// out-ranks the explicit `league/:id/<segment>` leaf, so React Router picks the
// global page. This is the same shadowing the `rankings` (removed shared
// children) and `tournament-category` (distinct segment) routes guard against.
// Currently `teams` (`…/league/:id/teams` must be LeagueTeamsPage, while the
// global TeamsPage stays reachable at platform/org scope, `…/teams`) and
// `audit-logs/:tabId?` (`…/league/:id/audit-logs[/:tabId]` must be
// LeagueAuditLogPage, while the global AuditLogsPage stays reachable at
// platform/org scope, `…/audit-logs[/:tabId]`). Unlike `teams` (a container
// node whose own `path` stays the bare segment, with `:tabId?` only on its
// nested `edit/:id/:tabId?` child), `audit-logs` carries its own optional tab
// param directly on the leaf route (`audit-logs/:tabId?`, mirroring
// `league/:id/rankings/:tabId?`) — so the literal `route.path` this set must
// match is the full `"audit-logs/:tabId?"` string, not just `"audit-logs"`.
// The tournament detail subtree no longer owns an `audit-logs` route, so the
// stripping is a flat filter with no entity-detail carve-out.
const withScopedPrefix = (children: RouteObject[]): RouteObject[] => [
  { path: SCOPED_ROUTE_PREFIX_LEAGUE, children: stripLeagueShadowedSegments(children) },
  { path: SCOPED_ROUTE_PREFIX_ORG, children },
  { path: SCOPED_ROUTE_PREFIX_PLATFORM, children },
  ...children,
];

const UsersPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/user/UsersPage/UsersPage"), "UsersPage"))
);

const AuditLogsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/auditLog/AuditLogsPage/AuditLogsPage"),
      "AuditLogsPage"
    )
  )
);

const DocumentationPage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/common/DocumentationPage"), "DocumentationPage")
  )
);

const ClubsPage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/club/ClubsPage/ClubsPage"), "PlatformClubsPage")
  )
);

const PlatformLeaguesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/league/LeaguesPage/LeaguesPage"),
      "PlatformLeaguesPage"
    )
  )
);

const OrganizationLeaguesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/league/LeaguesPage/LeaguesPage"),
      "OrganizationLeaguesPage"
    )
  )
);

const LeaguePage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/league/LeaguePage/LeaguePage"), "LeaguePage")
  )
);

const LeagueAthletesPage = Loadable(
  lazy(async () => {
    const { LeagueAthletesPage } = await import("../pages/league/LeagueAthletesPage");
    return { default: LeagueAthletesPage };
  })
);

const LeagueTeamsPage = Loadable(
  lazy(async () => {
    const { LeagueTeamsPage } = await import("../pages/league/LeagueTeamsPage");
    return { default: LeagueTeamsPage };
  })
);

const LeagueRankingsPage = Loadable(
  lazy(async () => {
    const { LeagueRankingsPage } = await import("../pages/league/LeagueRankingsPage");
    return { default: LeagueRankingsPage };
  })
);

const LeagueResultsPage = Loadable(
  lazy(async () => {
    const { LeagueResultsPage } = await import("../pages/league/LeagueResultsPage");
    return { default: LeagueResultsPage };
  })
);

const LeagueAuditLogPage = Loadable(
  lazy(async () => {
    const { LeagueAuditLogPage } = await import("../pages/league/LeagueAuditLogPage");
    return { default: LeagueAuditLogPage };
  })
);

const LeagueOnboardingPage = Loadable(
  lazy(async () => {
    const { LeagueOnboardingPage } = await import("../pages/league/LeagueOnboardingPage");
    return { default: LeagueOnboardingPage };
  })
);

const CreateLeaguePage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/league/CreateLeaguePage/CreateLeaguePage"),
      "CreateLeaguePage"
    )
  )
);

const UpdateLeaguePage = Loadable(
  lazy(async () => {
    const { UpdateLeaguePage } = await import("../pages/league/UpdateLeaguePage/UpdateLeaguePage");
    return { default: UpdateLeaguePage };
  })
);

const PurchasedLicensesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/purchasedLicense/PurchasedLicensesPage/PurchasedLicensesPage"),
      "PurchasedLicensesPage"
    )
  )
);

const LicensesPage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/license/LicensesPage/LicensesPage"), "LicensesPage")
  )
);

const CertificatesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/LicensesPage/CertificatesPage"),
      "CertificatesPage"
    )
  )
);

const ApplicationFormsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/LicensesPage/ApplicationFormsPage"),
      "ApplicationFormsPage"
    )
  )
);

const CreateLicensePage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/CreateLicensePage/CreateLicensePage"),
      "CreateLicensePage"
    )
  )
);

const CreateCertificatePage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/CreateLicensePage/CreateCertificatePage"),
      "CreateCertificatePage"
    )
  )
);

const CreateApplicationFormPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/CreateLicensePage/CreateApplicationFormPage"),
      "CreateApplicationFormPage"
    )
  )
);

const UpdateLicensePage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/UpdateLicensePage/UpdateLicensePage"),
      "UpdateLicensePage"
    )
  )
);

const UpdateCertificatePage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/UpdateLicensePage/UpdateCertificatePage"),
      "UpdateCertificatePage"
    )
  )
);

const UpdateApplicationFormPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/license/UpdateLicensePage/UpdateApplicationFormPage"),
      "UpdateApplicationFormPage"
    )
  )
);

const UpdateUserPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/user/UpdateUserPage/UpdateUserPage"),
      "UpdateUserPage"
    )
  )
);

const CreateUserPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/user/CreateUserPage"), "CreateUserPage"))
);

const CreateTournamentCategoryPage = Loadable(
  lazy(async () => {
    const { CreateTournamentCategoryPage } = await import(
      "../pages/tournamentCategory/CreateTournamentCategoryPage"
    );
    return { default: CreateTournamentCategoryPage };
  })
);

const UpdateTournamentCategoryPage = Loadable(
  lazy(async () => {
    const { UpdateTournamentCategoryPage } = await import(
      "../pages/tournamentCategory/UpdateTournamentCategoryPage"
    );
    return { default: UpdateTournamentCategoryPage };
  })
);

const BulkUpdateTournamentCategoriesPage = Loadable(
  lazy(async () => {
    const { BulkUpdateTournamentCategoriesPage } = await import(
      "../pages/tournamentCategory/BulkUpdateTournamentCategoriesPage"
    );
    return { default: BulkUpdateTournamentCategoriesPage };
  })
);

const CreateRankingCategoryPage = Loadable(
  lazy(async () => {
    const { CreateRankingCategoryPage } = await import(
      "../pages/rankingCategory/CreateRankingCategoryPage"
    );
    return { default: CreateRankingCategoryPage };
  })
);

const UpdateRankingCategoryPage = Loadable(
  lazy(async () => {
    const { UpdateRankingCategoryPage } = await import(
      "../pages/rankingCategory/UpdateRankingCategoryPage"
    );
    return { default: UpdateRankingCategoryPage };
  })
);

const RankingPointPage = Loadable(
  lazy(async () => {
    const { RankingPointPage } = await import("../pages/rankingPoint/RankingPointPage");
    return { default: RankingPointPage };
  })
);

const CreateRankingPointsBlockPage = Loadable(
  lazy(async () => {
    const { CreateRankingPointsBlockPage } = await import(
      "../pages/rankingPoint/CreateRankingPointsBlockPage"
    );
    return { default: CreateRankingPointsBlockPage };
  })
);

const UpdateRankingPointPage = Loadable(
  lazy(async () => {
    const { UpdateRankingPointPage } = await import("../pages/rankingPoint/UpdateRankingPointPage");
    return { default: UpdateRankingPointPage };
  })
);

const CreateTournamentCategoryGroupPage = Loadable(
  lazy(async () => {
    const { CreateTournamentCategoryGroupPage } = await import(
      "../pages/tournamentCategoryGroup/CreateTournamentCategoryGroupPage"
    );
    return { default: CreateTournamentCategoryGroupPage };
  })
);

const UpdateTournamentCategoryGroupPage = Loadable(
  lazy(async () => {
    const { UpdateTournamentCategoryGroupPage } = await import(
      "../pages/tournamentCategoryGroup/UpdateTournamentCategoryGroupPage"
    );
    return { default: UpdateTournamentCategoryGroupPage };
  })
);

const OrganizationsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/organization/OrganizationsPage/OrganizationsPage"),
      "OrganizationsPage"
    )
  )
);

const CreateOrganizationPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/organization/CreateOrganizationPage/CreateOrganizationPage"),
      "CreateOrganizationPage"
    )
  )
);

const UpdateOrganizationPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/organization/UpdateOrganizationPage/UpdateOrganizationPage"),
      "UpdateOrganizationPage"
    )
  )
);

const TournamentsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/tournament/TournamentsPage/TournamentsPage"),
      "TournamentsPage"
    )
  )
);

const TournamentMatchesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/tournament/TournamentMatchesPage/TournamentMatchesPage"),
      "TournamentMatchesPage"
    )
  )
);

const TournamentEntriesPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/tournament/TournamentEntriesPage/TournamentEntriesPage"),
      "TournamentEntriesPage"
    )
  )
);

const TournamentResultsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/tournament/TournamentResultsPage/TournamentResultsPage"),
      "TournamentResultsPage"
    )
  )
);

const TournamentTransactionsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/tournament/TournamentTransactionsPage/TournamentTransactionsPage"),
      "TournamentTransactionsPage"
    )
  )
);

const NewsPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/news/NewsPage/NewsPage"), "NewsPage"))
);

const CreateNewsPage = Loadable(
  lazy(async () => {
    const { CreateNewsPage } = await import("../pages/news/CreateNewsPage");
    return { default: CreateNewsPage };
  })
);

const UpdateNewsPage = Loadable(
  lazy(async () => {
    const { UpdateNewsPage } = await import("../pages/news/UpdateNewsPage");
    return { default: UpdateNewsPage };
  })
);

const BrowseCharts = Loadable(lazy(() => import("../pages/browse/BrowseCharts")));

const BrowseQuickStats = Loadable(lazy(() => import("../pages/browse/BrowseQuickStats")));

const Instructions = Loadable(lazy(() => import("../pages/dashboard/instructions")));
const Sports = Loadable(lazy(() => import("../pages/dashboard/Sports")));
const ThirdPartyLicense = Loadable(lazy(() => import("../pages/dashboard/thirdPartyLicense")));

const SportsCreate = Loadable(lazy(() => import("../pages/dashboard/SportsCreate")));
const CreateThirdPartyLicense = Loadable(
  lazy(() => import("../pages/dashboard/CreateThirdPartyLicense"))
);

// Dashboard pages

const Finance = Loadable(lazy(() => import("../pages/dashboard/Finance")));

const Overview = Loadable(lazy(() => import("../pages/dashboard/Overview")));
const PendingTournaments = Loadable(lazy(() => import("../pages/dashboard/pendingTournament")));

const TournamentResults = Loadable(lazy(() => import("../pages/dashboard/TournamentResults")));

// Error pages

const AuthorizationRequired = Loadable(lazy(() => import("../pages/AuthorizationRequired")));
const ServerError = Loadable(lazy(() => import("../pages/ServerError")));

//Leagues Pages
const LeaguesCreate = Loadable(lazy(() => import("../pages/dashboard/LeagueCreate")));
const ManagementTeamPage = Loadable(
  lazy(async () => {
    const { ManagementTeamPage } = await import(
      "../pages/managementTeam/ManagementTeamPage/ManagementTeamPage"
    );

    return { default: ManagementTeamPage };
  })
);

// Tour Categories
const TourCategories = Loadable(lazy(() => import("../pages/dashboard/TourCategories")));
const TourCatCategories = Loadable(lazy(() => import("../pages/dashboard/TourCatCreate")));

// Federation Account
const SubscriptionCreate = Loadable(lazy(() => import("../pages/dashboard/PricingCreate")));
const Subscriptions = Loadable(lazy(() => import("../pages/dashboard/subscription")));
const TransactionsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/transaction/TransactionsPage/TransactionsPage"),
      "TransactionsPage"
    )
  )
);

const StripeConnectsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/StripeConnectPage/StripeConnectsPage"),
      "StripeConnectsPage"
    )
  )
);

const PricingCreate = Loadable(lazy(() => import("../pages/dashboard/Pricing")));

const CoachesPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/coach/CoachesPage"), "CoachesPage"))
);
const Onboarding = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/dashboard/OnboardingPage"), "OnboardingPage")
  )
);

// The `onboarding` path is registered once and mounted under every scope prefix
// by `withScopedPrefix`. Under a league scope (`…/league/:leagueId/onboarding`)
// it must render the LEAGUE onboarding page; everywhere else the
// platform/federation one. An explicit `league/:id/onboarding` route can't win
// here — the league-prefixed copy of this shared `onboarding` child outranks it.
// eslint-disable-next-line react-refresh/only-export-components
const OnboardingRoute = () => {
  const league = useCurrentLeague();
  return league ? <LeagueOnboardingPage /> : <Onboarding />;
};

const ClubCreate = Loadable(lazy(() => import("../pages/dashboard/ClubsCreate")));
const CoachesCreate = Loadable(lazy(() => import("../pages/dashboard/CoachesCreate")));

//Rankings
const Rankings = Loadable(lazy(() => import("../pages/dashboard/Rankings")));

//Partners

const LeaguePartnersCreate = Loadable(lazy(() => import("../pages/dashboard/leaguePartnerCreate")));

const LeaguePartners = Loadable(lazy(() => import("../pages/dashboard/LeaguePartners")));

const PlatformDocumentsPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/platformDocument/PlatformDocumentsPage/PlatformDocumentsPage"),
      "PlatformDocumentsPage"
    )
  )
);
const UpdatePlatformPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/platform/UpdatePlatformPage/UpdatePlatformPage"),
      "UpdatePlatformPage"
    )
  )
);
const CreatePlatformPage = Loadable(
  lazy(async () => {
    const { CreatePlatformPage } = await import("../pages/platform/CreatePlatformPage");
    return { default: CreatePlatformPage };
  })
);
const AlbumsPage = Loadable(
  lazy(async () => {
    const { AlbumsPage } = await import("../pages/album/AlbumsPage");
    return { default: AlbumsPage };
  })
);
const LeagueAlbumsPage = Loadable(
  lazy(async () => {
    const { LeagueAlbumsPage } = await import("../pages/album/AlbumsPage");
    return { default: LeagueAlbumsPage };
  })
);
const CreateAlbumPage = Loadable(
  lazy(async () => {
    const { CreateAlbumPage } = await import("../pages/album/CreateAlbumPage");
    return { default: CreateAlbumPage };
  })
);
const UpdateAlbumPage = Loadable(
  lazy(async () => {
    const { UpdateAlbumPage } = await import("../pages/album/UpdateAlbumPage");
    return { default: UpdateAlbumPage };
  })
);

const PartnersPage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/partner/PartnersPage/PartnersPage"), "PartnersPage")
  )
);
const CreatePartnerPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/partner/CreatePartnerPage/CreatePartnerPage"),
      "CreatePartnerPage"
    )
  )
);
const UpdatePartnerPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/partner/UpdatePartnerPage/UpdatePartnerPage"),
      "UpdatePartnerPage"
    )
  )
);
const PartnerGroupsPage = Loadable(
  lazy(async () => {
    const { PartnerGroupsPage } = await import(
      "../pages/partnerGroup/PartnerGroupsPage/PartnerGroupsPage"
    );
    return { default: PartnerGroupsPage };
  })
);
const Pages = Loadable(lazy(() => import("../pages/dashboard/Pages")));
const BannersPage = Loadable(
  lazy(() =>
    importNamedComponent(() => import("../pages/banner/BannersPage/BannersPage"), "BannersPage")
  )
);
const CreateBannerPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/banner/CreateBannerPage/CreateBannerPage"),
      "CreateBannerPage"
    )
  )
);

const UpdateBannerPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/banner/UpdateBannerPage/UpdateBannerPage"),
      "UpdateBannerPage"
    )
  )
);

const CreatePlatformDocumentPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () =>
        import("../pages/platformDocument/CreatePlatformDocumentPage/CreatePlatformDocumentPage"),
      "CreatePlatformDocumentPage"
    )
  )
);

const UpdatePlatformDocumentPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () =>
        import("../pages/platformDocument/UpdatePlatformDocumentPage/UpdatePlatformDocumentPage"),
      "UpdatePlatformDocumentPage"
    )
  )
);

const DocumentCreate = Loadable(lazy(() => import("../pages/dashboard/createDocument")));

//Platforms
const Platforms = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/platform/PlatformsPage/PlatformsPage"),
      "PlatformsPage"
    )
  )
);
const PlatformSettingsLayout = Loadable(lazy(() => import("../pages/dashboard/PlatformCreate")));

// Teams
const TeamsPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/team/TeamsPage/TeamsPage"), "TeamsPage"))
);

const CreateTeamPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/team/CreateTeamPage"), "CreateTeamPage"))
);

const UpdateTeamPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/team/UpdateTeamPage"), "UpdateTeamPage"))
);

// Other pages
const Dashboard = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/common/DashboardPage/DashboardPage"),
      "DashboardPage"
    )
  )
);

// import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
// import { LoginPage } from "@/pages/auth/LoginPage/LoginPage";
// import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";

const LoginPage = Loadable(
  lazy(() => importNamedComponent(() => import("../pages/auth/LoginPage/LoginPage"), "LoginPage"))
);

const ForgotPasswordPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/auth/ForgotPasswordPage/ForgotPasswordPage"),
      "ForgotPasswordPage"
    )
  )
);

const ResetPasswordPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/auth/ResetPasswordPage/ResetPasswordPage"),
      "ResetPasswordPage"
    )
  )
);

const ManagerRedirectPage = Loadable(
  lazy(() =>
    importNamedComponent(
      () => import("../pages/auth/ManagerRedirectPage/ManagerRedirectPage"),
      "ManagerRedirectPage"
    )
  )
);

export const router = createBrowserRouter([
  {
    element: <Providers />,
    // Replaces `Providers` on an uncaught error; re-mounts the maintenance notice.
    ErrorBoundary: RootErrorBoundary,
    children: [
      {
        element: (
          <GuestGuard>
            <AuthSplashScreenProvider>
              <AuthLayout />
            </AuthSplashScreenProvider>
          </GuestGuard>
        ),
        children: [
          {
            path: "/login",
            element: <LoginPage />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPasswordPage />,
          },
          {
            path: "/reset-password",
            element: <ResetPasswordPage />,
          },
        ],
      },
      // Authenticated but deliberately OUTSIDE the platform/organization provider
      // chain: this route only mints a cross-app handoff token and leaves. It must
      // not be scope-prefixed either — `ScopedRouteEnforcer` would rewrite the URL
      // and re-render it under a different path.
      {
        path: MANAGER_REDIRECT_PATH,
        element: (
          <AuthGuard>
            <ManagerRedirectPage />
          </AuthGuard>
        ),
      },
      {
        element: (
          <AuthGuard>
            <CurrentUserProvider>
              <CurrentPlatformProvider>
                <TerminologyOverridesGate>
                  <CurrentOrganizationProvider>
                    <CurrentLeagueProvider>
                      <UserRolesProvider>
                        <OnboardingProvider>
                          <SplashScreenProvider>
                            <CurrentPlatformGuard>
                              <ScopedRouteEnforcer>
                                <TourProvider />
                              </ScopedRouteEnforcer>
                            </CurrentPlatformGuard>
                          </SplashScreenProvider>
                        </OnboardingProvider>
                      </UserRolesProvider>
                    </CurrentLeagueProvider>
                  </CurrentOrganizationProvider>
                </TerminologyOverridesGate>
              </CurrentPlatformProvider>
            </CurrentUserProvider>
          </AuthGuard>
        ),
        children: withScopedPrefix([
          {
            element: <DashboardLayout />,
            children: [
              {
                ErrorBoundary: RouteErrorBoundary,
                children: [
                  {
                    index: true,
                    element: <Dashboard />,
                  },
                  {
                    path: "statistics",
                    children: [
                      {
                        index: true,
                        element: <Dashboard />,
                      },
                    ],
                  },

                  {
                    path: "onboarding",
                    children: [
                      {
                        index: true,
                        element: <OnboardingRoute />,
                      },
                    ],
                  },

                  {
                    path: "league-tournaments",
                    children: [
                      {
                        index: true,
                        element: <Overview />,
                      },
                    ],
                  },

                  {
                    path: "pending-tournaments",
                    children: [
                      {
                        index: true,
                        element: <PendingTournaments />,
                      },
                    ],
                  },

                  {
                    path: "tournament-results",
                    children: [
                      {
                        index: true,
                        element: <TournamentResults />,
                      },
                    ],
                  },

                  {
                    path: "third-party-license",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuperAdminGuard>
                            <ThirdPartyLicense />
                          </SuperAdminGuard>
                        ),
                      },

                      {
                        path: "new/:id?",
                        element: (
                          <SuperAdminGuard>
                            <CreateThirdPartyLicense />
                          </SuperAdminGuard>
                        ),
                      },
                      {
                        path: "edit/:id?",
                        element: (
                          <SuperAdminGuard>
                            <CreateThirdPartyLicense />
                          </SuperAdminGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "platform-third-party-license",
                    children: [
                      {
                        index: true,
                        element: <ThirdPartyLicense />,
                      },
                    ],
                  },
                  {
                    path: "sports",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuperAdminGuard>
                            <Sports />
                          </SuperAdminGuard>
                        ),
                      },

                      {
                        path: "new/:id?",
                        element: (
                          <SuperAdminGuard>
                            <SportsCreate />
                          </SuperAdminGuard>
                        ),
                      },

                      {
                        path: "edit/:id?",
                        element: (
                          <SuperAdminGuard>
                            <SportsCreate />
                          </SuperAdminGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "platform-sports",
                    children: [
                      {
                        index: true,
                        element: <Sports />,
                      },
                    ],
                  },
                  {
                    path: "platform-disciplines",
                    children: [
                      {
                        index: true,
                        element: <Disciplines />,
                      },
                    ],
                  },
                  {
                    path: "disciplines",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuperAdminGuard>
                            <Disciplines />
                          </SuperAdminGuard>
                        ),
                      },

                      {
                        path: "new/:id?",
                        element: (
                          <SuperAdminGuard>
                            <DisciplineCreate />
                          </SuperAdminGuard>
                        ),
                      },
                      {
                        path: "edit/:id?",
                        element: (
                          <SuperAdminGuard>
                            <DisciplineCreate />
                          </SuperAdminGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "platform-leagues",
                    children: [
                      {
                        index: true,
                        element: <PlatformLeaguesPage basePath="/platform-leagues" />,
                      },
                      {
                        path: "new/:id?",
                        element: <LeaguesCreate />,
                      },
                      {
                        path: "edit/:id?",
                        element: <LeaguesCreate />,
                      },
                      // All/Leagues/Tours tab segment — ranked below the static
                      // `new` / `edit` children, so it only catches tab ids.
                      {
                        path: ":tabId",
                        element: <PlatformLeaguesPage basePath="/platform-leagues" />,
                      },
                    ],
                  },
                  {
                    path: "league/:id",
                    element: <LeaguePageRedirect />,
                  },
                  {
                    path: "league/:id/overview",
                    element: <LeaguePage />,
                  },
                  {
                    path: "league/:id/athletes",
                    element: <LeagueAthletesPage />,
                  },
                  {
                    path: "league/:id/teams",
                    element: <LeagueTeamsPage />,
                  },
                  // League-wide tournament results (`/league/:id/results`) —
                  // every result of every tournament in the league, so ranking
                  // results can be reviewed and published in one place.
                  // Registered as an explicit `league/:id/…` route like
                  // `athletes` above; `results` exists globally only as a child
                  // of `tournament/:tournamentId`, never as a top-level segment,
                  // so there is no shared block to strip here.
                  {
                    path: "league/:id/results",
                    element: <LeagueResultsPage />,
                  },
                  {
                    path: "league/:id/audit-logs/:tabId?",
                    element: <LeagueAuditLogPage />,
                  },
                  // League-scoped rankings page (`/league/:id/rankings/:tabId?`,
                  // tabId ∈ {rankings, history}, default 'rankings'). Registered
                  // as an explicit `league/:id/…` route — like `athletes` above —
                  // rather than a shared child, because the page is league-only
                  // (`useCurrentLeague()`); the legacy shared `rankings` /
                  // `rankings-history` children were removed so they can't shadow
                  // it. The legacy RankingDetails/RankingHistory pages are still
                  // rendered directly by the old dashboard leagueLayout, not via
                  // routes, so removing the route entries is safe.
                  {
                    path: "league/:id/rankings/:tabId?",
                    element: <LeagueRankingsPage />,
                  },

                  // League-scoped tournament-category create/edit. The explicit
                  // `league/:leagueId/…` child path matches under the org prefix
                  // (like `league/:id/overview`), so the canonical URL is
                  // `/platform/:p/entity/:o/league/:leagueId/tournament-category/…`
                  // and the pages read `leagueId` / `id` / `tabId` from the params.
                  // NOTE: the segment is the SINGULAR `tournament-category`. It
                  // started that way to avoid the legacy dashboard form's plural
                  // `tournament-categories` block shadowing these routes; that
                  // block is gone now, but the singular URLs are the shipped
                  // contract, so they stay.
                  {
                    path: "league/:leagueId/tournament-category/new",
                    element: <CreateTournamentCategoryPage />,
                  },
                  {
                    path: "league/:leagueId/tournament-category/edit/:id/:tabId?",
                    element: <UpdateTournamentCategoryPage />,
                  },
                  // Bulk edit of a selection made on the league's categories tab.
                  // The ids travel in router state + sessionStorage, never in the
                  // URL — hundreds of `cat_…` ids do not fit in a query string.
                  {
                    path: "league/:leagueId/tournament-category/bulk-edit/:tabId?",
                    element: <BulkUpdateTournamentCategoriesPage />,
                  },

                  // League-scoped ranking-category create/edit — same rationale as
                  // the tournament-category block above: the SINGULAR
                  // `ranking-category` segment is used on purpose so the legacy
                  // plural `ranking-categories` dashboard form (below) can't shadow
                  // these new-design routes.
                  {
                    path: "league/:leagueId/ranking-category/new",
                    element: <CreateRankingCategoryPage />,
                  },
                  {
                    path: "league/:leagueId/ranking-category/edit/:id/:tabId?",
                    element: <UpdateRankingCategoryPage />,
                  },

                  // League-scoped ranking-POINT block detail. Same singular-segment
                  // rationale as the blocks above (the plural `ranking-points` is
                  // shadowed by the legacy auto-mounted dashboard route below). No
                  // `:tabId?` — the page is a single card, not a tabbed form. `:id`
                  // is the block's NUMERIC id (blocks have no public id).
                  {
                    path: "league/:leagueId/ranking-point/edit/:id",
                    element: <RankingPointPage />,
                  },

                  // League-scoped ranking-point BLOCK settings create/edit. Same
                  // singular-segment rationale as the blocks above: the segment is
                  // `ranking-point-block`, which is distinct from both the legacy
                  // auto-mounted plural `ranking-points` dashboard route (which
                  // would shadow it) and from the `ranking-point` positions route
                  // right above — those two surfaces edit the same entity but are
                  // separate pages, so they need separate paths. `:id` is the
                  // block's NUMERIC id (blocks have no public id); `:tabId?`
                  // selects the settings tab (general / additionalRules).
                  {
                    path: "league/:leagueId/ranking-point-block/new",
                    element: <CreateRankingPointsBlockPage />,
                  },
                  {
                    path: "league/:leagueId/ranking-point-block/edit/:id/:tabId?",
                    element: <UpdateRankingPointPage />,
                  },

                  // League-scoped tournament-category-GROUP create/edit. Same
                  // singular-segment rationale as the two blocks above. No
                  // `:tabId?` — the group form is a single step (name +
                  // categories), so there are no tabs. `:id` is the group's
                  // NUMERIC id (groups have no public id).
                  {
                    path: "league/:leagueId/tournament-category-group/new",
                    element: <CreateTournamentCategoryGroupPage />,
                  },
                  {
                    path: "league/:leagueId/tournament-category-group/edit/:id",
                    element: <UpdateTournamentCategoryGroupPage />,
                  },

                  {
                    path: "ranking-categories",
                    children: [
                      {
                        index: true,
                        element: <TourCategories />,
                      },
                      {
                        path: "new/:id?",
                        element: <TourCatCategories />,
                      },
                      {
                        path: "edit/:id?",
                        element: <TourCatCategories />,
                      },
                    ],
                  },
                  {
                    path: "ranking-points",
                    children: [
                      {
                        index: true,
                        element: <Rankings />,
                      },
                    ],
                  },

                  {
                    path: "organization_leagues",
                    children: [
                      {
                        index: true,
                        element: <OrganizationLeaguesPage basePath="/organization_leagues" />,
                      },
                      // All/Leagues/Tours tab segment.
                      {
                        path: ":tabId",
                        element: <OrganizationLeaguesPage basePath="/organization_leagues" />,
                      },
                    ],
                  },

                  {
                    path: "pricing",
                    children: [
                      {
                        index: true,
                        element: <PricingCreate />,
                      },
                      {
                        path: "new/:id?",
                        element: <SubscriptionCreate />,
                      },
                    ],
                  },
                  {
                    path: "subscriptions",
                    children: [
                      {
                        index: true,
                        element: <Subscriptions />,
                      },
                    ],
                  },

                  {
                    path: "league-partners",
                    children: [
                      {
                        index: true,
                        element: <LeaguePartners />,
                      },
                      {
                        path: "new/:id?",
                        element: <LeaguePartnersCreate />,
                      },
                      {
                        path: "edit/:id?",
                        element: <LeaguePartnersCreate />,
                      },
                    ],
                  },

                  {
                    path: "finance",
                    element: <Finance />,
                  },

                  {
                    path: "entities",
                    children: [
                      {
                        index: true,
                        element: <OrganizationsPage />,
                      },
                      {
                        path: "new",
                        element: <CreateOrganizationPage />,
                      },
                      {
                        path: "edit/current/:tabId?",
                        element: <UpdateOrganizationPage />,
                      },
                    ],
                  },

                  {
                    path: "partners",
                    children: [
                      {
                        index: true,
                        element: <PartnersPage />,
                      },
                      {
                        path: "new",
                        element: <CreatePartnerPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdatePartnerPage />,
                      },
                      {
                        path: "groups",
                        element: <PartnerGroupsPage />,
                      },
                    ],
                  },

                  {
                    path: "pages",
                    element: <Pages />,
                  },

                  {
                    path: "banners",
                    children: [
                      {
                        index: true,
                        element: <BannersPage />,
                      },
                      {
                        path: "new",
                        element: <CreateBannerPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdateBannerPage />,
                      },
                    ],
                  },

                  {
                    path: "coaches",
                    children: [
                      {
                        index: true,
                        element: <CoachesPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <CoachesCreate />,
                      },
                    ],
                  },

                  {
                    path: "tours",
                    children: [
                      {
                        index: true,
                        element: <OrganizationLeaguesPage basePath="/tours" />,
                      },
                      {
                        path: "settings/:id?",
                        element: <LeaguesCreate />,
                      },
                      {
                        path: "new",
                        element: <CreateLeaguePage naming="tour" />,
                      },
                      {
                        path: "instructions",
                        element: <Instructions />,
                      },
                      // All/Leagues/Tours tab segment — ranked below the static
                      // `settings` / `new` / `instructions` children, so it only
                      // catches tab ids (e.g. `/tours/all`, `/tours/leagues`).
                      {
                        path: ":tabId",
                        element: <OrganizationLeaguesPage basePath="/tours" />,
                      },
                    ],
                  },
                  {
                    path: "leagues/new",
                    element: <CreateLeaguePage naming="league" />,
                  },
                  // League-scoped edit: reads the active league from `useCurrentLeague`
                  // (URL scope), mirroring `entities/edit/current`. `withScopedPrefix`
                  // mounts this under the league prefix, so the full URL is
                  // `/platform/:p/entity/:o/league/:l/edit/:tabId?`.
                  {
                    path: "edit/:tabId?",
                    element: <UpdateLeaguePage />,
                  },

                  {
                    path: "management-team",
                    element: <ManagementTeamPage />,
                  },

                  {
                    path: "operators",
                    children: [
                      {
                        index: true,
                        element: <ClubsPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <ClubCreate />,
                      },
                      {
                        path: "edit/:id?",
                        element: <ClubCreate />,
                      },
                    ],
                  },

                  // News surfaces under `/news` at every scope (platform /
                  // organization / league — the league sidebar links to
                  // `/league/:id/news`). `withScopedPrefix` registers this block
                  // under all three prefixes; the adaptive `NewsPage` picks the
                  // scope from the active league / organization / platform (see
                  // NewsPage.tsx).
                  {
                    path: "news",
                    children: [
                      {
                        index: true,
                        element: <NewsPage tabId="all" />,
                      },
                      {
                        path: "all",
                        element: <NewsPage tabId="all" />,
                      },
                      {
                        path: "published",
                        element: <NewsPage tabId="published" />,
                      },
                      {
                        path: "drafts",
                        element: <NewsPage tabId="drafts" />,
                      },
                      {
                        path: "scheduled",
                        element: <NewsPage tabId="scheduled" />,
                      },
                      {
                        path: "tagged",
                        element: <NewsPage tabId="tagged" />,
                      },
                      {
                        path: "shared",
                        element: <NewsPage tabId="shared" />,
                      },
                      {
                        path: "new",
                        element: <CreateNewsPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdateNewsPage />,
                      },
                    ],
                  },

                  // Platform / organization scope. `AlbumsPage` locks to the
                  // active organization when one is selected, otherwise the
                  // platform (see AlbumsPage.tsx).
                  {
                    path: "albums",
                    children: [
                      {
                        index: true,
                        element: <AlbumsPage tabId="all" />,
                      },
                      {
                        path: "all",
                        element: <AlbumsPage tabId="all" />,
                      },
                      {
                        path: "published",
                        element: <AlbumsPage tabId="published" />,
                      },
                      {
                        path: "drafts",
                        element: <AlbumsPage tabId="drafts" />,
                      },
                      {
                        path: "scheduled",
                        element: <AlbumsPage tabId="scheduled" />,
                      },
                      {
                        path: "tagged",
                        element: <AlbumsPage tabId="tagged" />,
                      },
                      {
                        path: "shared",
                        element: <AlbumsPage tabId="shared" />,
                      },
                      {
                        path: "new",
                        element: <CreateAlbumPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdateAlbumPage />,
                      },
                    ],
                  },

                  // League-scoped gallery: the league sidebar links to
                  // `/league/:id/gallery` (see useLeagueNavGroups). `withScopedPrefix`
                  // auto-registers this under the league prefix, so the page scopes
                  // itself to the league (see AlbumsPage.tsx / useAlbumsQueryFilter).
                  {
                    path: "gallery",
                    children: [
                      {
                        index: true,
                        element: <LeagueAlbumsPage tabId="all" />,
                      },
                      {
                        path: "all",
                        element: <LeagueAlbumsPage tabId="all" />,
                      },
                      {
                        path: "published",
                        element: <LeagueAlbumsPage tabId="published" />,
                      },
                      {
                        path: "drafts",
                        element: <LeagueAlbumsPage tabId="drafts" />,
                      },
                      {
                        path: "scheduled",
                        element: <LeagueAlbumsPage tabId="scheduled" />,
                      },
                      {
                        path: "tagged",
                        element: <LeagueAlbumsPage tabId="tagged" />,
                      },
                      {
                        path: "shared",
                        element: <LeagueAlbumsPage tabId="shared" />,
                      },
                      {
                        path: "new",
                        element: <CreateAlbumPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdateAlbumPage />,
                      },
                    ],
                  },

                  {
                    path: "platforms",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuperAdminGuard>
                            <Platforms />
                          </SuperAdminGuard>
                        ),
                      },
                      {
                        // Create flow → new REST-backed wizard. Super-admin only
                        // (the page also self-gates via `useCurrentUser`), wrapped
                        // in `SuperAdminGuard` to match the `platforms` index above.
                        path: "new",
                        element: (
                          <SuperAdminGuard>
                            <CreatePlatformPage />
                          </SuperAdminGuard>
                        ),
                      },
                      {
                        // Legacy edit-by-id flow: `/platforms/new/:id` carries a real
                        // `plt_…` public id, switches into that platform and renders
                        // the legacy settings layout (still linked from `myPlatforms`).
                        // Kept distinct from the `new` create route above.
                        path: "new/:id",
                        element: <PlatformSettingsLayout />,
                      },
                      {
                        path: "edit/current",
                        element: <PlatformSettingsLayout />,
                      },
                      {
                        path: "settings/current",
                        element: <PlatformSettingsLayout />,
                      },
                      {
                        path: "manage/current/:tabId?",
                        element: <UpdatePlatformPage />,
                      },
                    ],
                  },

                  {
                    path: "stripe-accounts",
                    children: [
                      {
                        index: true,
                        element: (
                          <SuperAdminGuard>
                            <StripeConnectsPage applyPlatformFilter={false} />
                          </SuperAdminGuard>
                        ),
                      },
                    ],
                  },

                  {
                    path: "platform-stripe-accounts",
                    children: [
                      { index: true, element: <StripeConnectsPage applyPlatformFilter={true} /> },
                    ],
                  },

                  {
                    path: "tournaments",
                    children: [
                      {
                        index: true,
                        element: <TournamentsPage basePath="/tournaments" tabId="all" />,
                      },
                      {
                        path: "all",
                        element: <TournamentsPage basePath="/tournaments" tabId="all" />,
                      },
                      {
                        path: "inProgressUpcoming",
                        element: (
                          <TournamentsPage basePath="/tournaments" tabId="inProgressUpcoming" />
                        ),
                      },
                      {
                        path: "completed",
                        element: <TournamentsPage basePath="/tournaments" tabId="completed" />,
                      },
                      {
                        path: "waitingConfirmation",
                        element: (
                          <TournamentsPage basePath="/tournaments" tabId="waitingConfirmation" />
                        ),
                      },
                      {
                        path: ":tournamentId",
                        loader: redirectLegacyTournamentDetail,
                      },
                      {
                        path: ":tournamentId/*",
                        loader: redirectLegacyTournamentDetail,
                      },
                    ],
                  },

                  {
                    path: "tournament/:tournamentId",
                    children: [
                      {
                        index: true,
                        loader: redirectTournamentRootToManager,
                      },
                      {
                        path: "matches/:tabId?",
                        element: <TournamentMatchesPage />,
                      },
                      {
                        path: "entries",
                        element: <TournamentEntriesPage />,
                      },
                      {
                        path: "results",
                        element: <TournamentResultsPage />,
                      },
                      {
                        path: "transactions",
                        element: <TournamentTransactionsPage />,
                      },
                    ],
                  },

                  {
                    path: "users",
                    children: [
                      {
                        index: true,
                        element: <UsersPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <CreateUserPage />,
                      },
                      {
                        path: "edit/:id/:tabId?",
                        element: <UpdateUserPage />,
                      },
                    ],
                  },

                  {
                    path: "transactions",
                    children: [
                      {
                        index: true,
                        element: <TransactionsPage basePath="/transactions" />,
                      },
                      {
                        path: ":tabId",
                        element: <TransactionsPage basePath="/transactions" />,
                      },
                    ],
                  },

                  {
                    path: "licenses",
                    children: [
                      {
                        index: true,
                        element: <LicensesPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <CreateLicensePage />,
                      },
                      {
                        path: "edit/:licenseId/:tabId?",
                        element: <UpdateLicensePage />,
                      },
                    ],
                  },
                  {
                    path: "certificates",
                    children: [
                      {
                        index: true,
                        element: <CertificatesPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <CreateCertificatePage />,
                      },
                      {
                        path: "edit/:licenseId/:tabId?",
                        element: <UpdateCertificatePage />,
                      },
                    ],
                  },
                  {
                    path: "application-forms",
                    children: [
                      {
                        index: true,
                        element: <ApplicationFormsPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <CreateApplicationFormPage />,
                      },
                      {
                        path: "edit/:licenseId/:tabId?",
                        element: <UpdateApplicationFormPage />,
                      },
                    ],
                  },
                  {
                    path: "issued-licenses",
                    children: [
                      {
                        index: true,
                        element: <PurchasedLicensesPage basePath="/issued-licenses" />,
                      },
                      {
                        path: ":tabId",
                        element: <PurchasedLicensesPage basePath="/issued-licenses" />,
                      },
                    ],
                  },
                  {
                    path: "issued-certificates",
                    children: [
                      {
                        index: true,
                        element: (
                          <PurchasedLicensesPage
                            type="certificate"
                            basePath="/issued-certificates"
                          />
                        ),
                      },
                      {
                        path: ":tabId",
                        element: (
                          <PurchasedLicensesPage
                            type="certificate"
                            basePath="/issued-certificates"
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: "submitted-application-forms",
                    children: [
                      {
                        index: true,
                        element: (
                          <PurchasedLicensesPage
                            type="applicationForm"
                            basePath="/submitted-application-forms"
                          />
                        ),
                      },
                      {
                        path: ":tabId",
                        element: (
                          <PurchasedLicensesPage
                            type="applicationForm"
                            basePath="/submitted-application-forms"
                          />
                        ),
                      },
                    ],
                  },

                  {
                    path: "teams",
                    children: [
                      {
                        index: true,
                        element: <TeamsPage />,
                      },
                      {
                        path: "new",
                        element: <CreateTeamPage />,
                      },
                      {
                        path: "edit/:id/:tabId?",
                        element: <UpdateTeamPage />,
                      },
                    ],
                  },

                  {
                    path: "documents",
                    children: [
                      {
                        index: true,
                        element: <PlatformDocumentsPage basePath="/documents" />,
                      },
                      {
                        path: ":tabId",
                        element: <PlatformDocumentsPage basePath="/documents" />,
                      },
                      {
                        path: "new",
                        element: <CreatePlatformDocumentPage />,
                      },
                      {
                        path: "edit/:id",
                        element: <UpdatePlatformDocumentPage />,
                      },
                      {
                        path: "new/:id?",
                        element: <DocumentCreate />,
                      },
                    ],
                  },

                  {
                    path: "documentation",
                    element: <DocumentationPage />,
                  },

                  {
                    path: "audit-logs/:tabId?",
                    element: <AuditLogsPage />,
                  },

                  {
                    path: "*",
                    element: <NotFoundPage />,
                  },
                ],
              },
            ],
          },
        ]),
      },

      {
        path: "browse",
        element: <BrowseLayout />,
        children: [
          {
            index: true,
            element: <BrowseCharts />,
          },

          {
            path: "data-display/quick-stats",
            element: <BrowseQuickStats />,
          },
        ],
      },

      {
        path: "401",
        element: <AuthorizationRequired />,
      },
      {
        path: "500",
        element: <ServerError />,
      },
    ],
  },
]);
